import {
  RouterLink,
  RouterLinkAction,
} from '@/utilities/redux-first-router.export';
import { ReactNode } from 'react';

import { KeyMetric } from '@/components/KeyMetric';
import { NarrativeAuthorBase } from '@/components/NarrativeAuthor';
import { toCurrencyTruncated, toTruncated } from '@/utilities/formatting';
import { CurrencyISO } from '@/constants/currencies';
import PriceTargetOutline from '@simplywallst/ui-core/icons/PriceTargetOutline';
import WarrenSolid from '@simplywallst/ui-core/icons/WarrenSolid';
import { CompanyIntrinsicDiscountBase } from '@/components/CompanyIntrinsicDiscount';
import { cn } from '@simplywallst/ui-core/next';
import { NarrativeCardDropdown } from '../NarrativeCardDropdown';

type Props = {
  children?: ReactNode;
  linkTo?: string | RouterLinkAction | undefined;
  onClick?: () => void;
  className?: string;
  title: ReactNode;
  type: 'DCF' | 'ANALYSTS';
  currencyIso: CurrencyISO;
  priceTargetAnalystCount: number | null | undefined;
  fairValue: number | null;
  intrinsicDiscount: number | null;
  narrativeId: string;
};

const iconWrapperClasses = cn(
  'grid size-8 items-center justify-center bg-p-grey-10'
);
const getTypeSpecificProps = (type: Props['type']) => {
  if (type === 'DCF') {
    return {
      label: 'Discounted Cash Flow',
      subtitle: 'Simply Wall St',
      icon: (
        <div className={iconWrapperClasses}>
          <WarrenSolid className="w-6" />
        </div>
      ),
    };
  }

  return {
    label: 'Analyst Price Target',
    subtitle: 'Equity Analysts',
    icon: (
      <div className={iconWrapperClasses}>
        <PriceTargetOutline className="w-6 fill-accent" />
      </div>
    ),
  };
};

export const NarrativeCardSynthetic = ({
  children,
  onClick,
  linkTo,
  title,
  type,
  priceTargetAnalystCount,
  fairValue,
  intrinsicDiscount,
  currencyIso,
  className,
  narrativeId,
}: Props) => {
  const { icon, subtitle, label } = getTypeSpecificProps(type);

  return (
    <div
      className={cn('max-h-[405px] overflow-hidden rounded-x1_5', className)}
    >
      <div className="grid max-h-[235px] min-h-[235px] gap-4  bg-surface-3 p-4">
        <div className="grid grid-cols-[1fr_max-content] gap-2">
          <NarrativeAuthorBase title={label} subtitle={subtitle}>
            {icon}
          </NarrativeAuthorBase>
          <NarrativeCardDropdown
            narrativeId={narrativeId}
            valuationType={type}
          />
        </div>

        <RouterLink
          className="block min-h-[81px] no-underline decoration-p-white hover:underline focus:underline"
          to={linkTo}
          onClick={onClick}
        >
          <h4 className="m-0 line-clamp-3 text-base font-strong text-solid">
            {title}
          </h4>
        </RouterLink>
      </div>
      <div className="grid gap-4 bg-surface-2 p-4">
        <div className="grid grid-cols-[max-content_max-content] gap-2">
          <KeyMetric color="chart03">
            <div className="grid grid-rows-[max-content_max-content]">
              <div className="text-lg font-strong text-solid">
                {toCurrencyTruncated(fairValue || 0, 2, currencyIso ?? 'XXX')}
              </div>
              <div className="grid grid-cols-[max-content_max-content] items-center gap-1 text-tiny">
                <div className="text-soft">FV</div>

                <CompanyIntrinsicDiscountBase discount={intrinsicDiscount} />
              </div>
            </div>
          </KeyMetric>
          {typeof priceTargetAnalystCount === 'number' && (
            <KeyMetric color="brand01">
              <div className="grid grid-rows-[max-content_max-content]">
                <div className="text-lg font-strong text-solid">
                  {toTruncated(priceTargetAnalystCount, 0)}
                </div>
                <div className="text-tiny text-soft">Number of Analysts</div>
              </div>
            </KeyMetric>
          )}
        </div>
        {children}

        <div className="grid justify-end text-xs text-softer">
          Yesterday
          <div className="sr-only">SWS updated this narrative</div>
        </div>
      </div>
    </div>
  );
};
